import {copyText} from '@github-ui/copy-to-clipboard'
import {useMutation} from '@github-ui/react-query'
import {isStaff} from '@github-ui/stats'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon} from '@primer/octicons-react'
import {Banner, Button, Label, Link, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'

import {apiDeleteRecap} from '../../api/scribe/api-delete-recap'
import {apiGenerateRecap} from '../../api/scribe/api-generate-recap'
import type {PromptOptions, Recap} from '../../api/scribe/types'
import {useMarkdownPreview} from '../../hooks/use-markdown-preview'
import {useRecapsQuery} from '../../queries/use-recaps-query'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import contentStyles from './content.module.css'
import {SidePanelToolbar} from './header'
import {RecapArchive} from './recap-archive'
import styles from './recap-content.module.css'
import {RecapInsights} from './recap-insights'
import {RecapReport} from './recap-report'
import {RecapSummary} from './recap-summary'

function recapToMarkdown(recap: Recap): string {
  let markdownContent = ''

  if (recap.insights && recap.insights.length > 0) {
    markdownContent += '## Insights\n\n'
    for (const insight of recap.insights) {
      markdownContent += `- ${insight.content}\n`
    }
    markdownContent += '\n'
  }

  if (recap.report && recap.report.content) {
    markdownContent += '## Report\n\n'
    markdownContent += recap.report.content.replace(/\\n/g, '\n')
  }

  return markdownContent
}

export const RecapContent = memo(function RecapContentComponent() {
  const projectRouteParams = useProjectRouteParams()
  const {
    data: recaps,
    isLoading,
    error,
    refetch,
  } = useRecapsQuery({
    variables: projectRouteParams,
    refetchInterval: 5000, // Poll every 5 seconds
  })
  const [selectedTab, setSelectedTab] = useState<'latest' | 'archive'>('latest')
  const {html: reportHtml, loadPreview} = useMarkdownPreview()
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null)
  const [selectedRecapId, setSelectedRecapId] = useState<number | null>(null)

  const feedbackUrl = isStaff()
    ? 'https://gh.io/copilot-reporting-project-feedback'
    : 'https://gh.io/copilot-reporting-project-feedback-external'

  const latestRecap = useMemo(() => {
    if (selectedRecapId !== null && recaps) {
      const found = recaps.find(recap => recap.id === selectedRecapId)
      return found || null
    }
    return recaps && recaps.length ? recaps[0] : null
  }, [selectedRecapId, recaps])
  const hasValidData =
    latestRecap &&
    ((latestRecap.insights && latestRecap.insights.length > 0) || (latestRecap.report && latestRecap.report.content))

  useEffect(() => {
    if (latestRecap?.report?.content) {
      const unescapedContent = latestRecap.report.content.replace(/\\n/g, '\n')
      loadPreview(unescapedContent)
    }
  }, [latestRecap, loadPreview])

  const {mutate: generateRecap, isPending: isGeneratingRecap} = useMutation({
    mutationFn: apiGenerateRecap,
    onSuccess: () => refetch(),
  })

  const {mutate: deleteRecap} = useMutation({
    mutationFn: apiDeleteRecap,
    onSuccess: () => {
      setSelectedRecapId(null)
      refetch()
    },
  })

  const handleGenerateRecap = useCallback(
    (filterQuery: string, promptOptions?: PromptOptions) => {
      setGenerationStartTime(Date.now())
      generateRecap({...projectRouteParams, filterQuery, promptOptions})
    },
    [generateRecap, projectRouteParams],
  )

  const handleViewRecap = useCallback((recapId: number) => {
    setGenerationStartTime(null)
    setSelectedRecapId(recapId)
    setSelectedTab('latest')
  }, [])

  const handleSelectLatestTab = useCallback(() => {
    setSelectedTab('latest')
    setSelectedRecapId(null)
  }, [])

  const handleSelectArchiveTab = useCallback(() => {
    setSelectedTab('archive')
  }, [])

  const handleRecapCopy = useCallback(() => {
    if (!latestRecap) return
    const markdownContent = recapToMarkdown(latestRecap)
    void copyText(markdownContent)
  }, [latestRecap])

  const handleDeleteRecap = useCallback(
    (recapId: number) => {
      deleteRecap({...projectRouteParams, recapId})
    },
    [deleteRecap, projectRouteParams],
  )

  const freshRecap = useMemo(() => {
    if (!latestRecap?.created_at) return false

    if (generationStartTime) {
      const recapTime = new Date(latestRecap.created_at).getTime()
      return recapTime > generationStartTime
    } else {
      return latestRecap?.done
    }
  }, [generationStartTime, latestRecap])

  const showLoadingSpinner = useMemo(() => {
    if (generationStartTime === null) return false
    if (!freshRecap) return true

    return !latestRecap?.done
  }, [generationStartTime, freshRecap, latestRecap])

  const freshInsightData = useMemo(() => {
    if (!freshRecap) return false

    return latestRecap?.insights && latestRecap.insights.length > 0
  }, [freshRecap, latestRecap])

  const freshReportData = useMemo(() => {
    if (!freshRecap) return false

    return latestRecap?.report && reportHtml
  }, [freshRecap, latestRecap, reportHtml])

  const noRecapsAvailable = !isLoading && !error && !showLoadingSpinner && !latestRecap && !freshRecap

  return (
    <div className={contentStyles.infoPanelContainer} {...testIdProps('side-panel-recap-content')}>
      <div className={contentStyles.infoPanelContent}>
        <div className={contentStyles.infoPanelHeader}>
          <SidePanelToolbar showCloseButton>
            <div className={styles.infoPanelLinks}>
              {isStaff() && <Label variant="accent">Staff</Label>}
              <Link
                muted
                href={feedbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Feedback"
                className={styles.feedbackLink}
              >
                Feedback
              </Link>
            </div>
          </SidePanelToolbar>
          <div className={contentStyles.headerSpacer} />
          <h2>Recap</h2>
        </div>

        <Banner title="Project recaps will be shut off on August 28" variant="warning" layout="compact" flush />

        <div role="tablist" aria-label="Recap tabs" className={styles.tabList}>
          <Button
            role="tab"
            aria-selected={selectedTab === 'latest'}
            aria-controls="latest-panel"
            id="latest-tab"
            onClick={handleSelectLatestTab}
            variant={selectedTab === 'latest' ? 'default' : 'invisible'}
            className={styles.tab}
            tabIndex={selectedTab === 'latest' ? 0 : -1}
          >
            Latest
          </Button>
          <Button
            role="tab"
            aria-selected={selectedTab === 'archive'}
            aria-controls="archive-panel"
            id="archive-tab"
            onClick={handleSelectArchiveTab}
            variant={selectedTab === 'archive' ? 'default' : 'invisible'}
            className={styles.tab}
            tabIndex={selectedTab === 'archive' ? 0 : -1}
          >
            Archive
          </Button>
        </div>

        {selectedTab === 'latest' && (
          <div role="tabpanel" id="latest-panel" aria-labelledby="latest-tab">
            {(showLoadingSpinner || hasValidData) && (
              <div className={styles.recapsContent}>
                {showLoadingSpinner && (
                  <Button disabled leadingVisual={<Spinner size="small" />} className={styles.generateRecapLargeButton}>
                    Generating recap...
                  </Button>
                )}

                <RecapSummary
                  showLoadingSpinner={showLoadingSpinner}
                  latestRecap={latestRecap}
                  onRegenerate={handleGenerateRecap}
                  onCopy={handleRecapCopy}
                  isGeneratingRecap={isGeneratingRecap}
                />

                <RecapInsights
                  showLoadingSpinner={showLoadingSpinner}
                  freshInsightData={!!freshInsightData}
                  latestRecap={latestRecap}
                />

                <RecapReport
                  showLoadingSpinner={showLoadingSpinner}
                  freshReportData={!!freshReportData}
                  reportHtml={reportHtml}
                  latestRecap={latestRecap}
                />
              </div>
            )}

            {noRecapsAvailable && (
              <div className={styles.insightCard}>
                <Blankslate>
                  <Blankslate.Visual>
                    <BookIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>Turn recent activity into insights</Blankslate.Heading>
                  <Blankslate.Description>
                    Get a quick summary and actionable insights from recent work in this project.
                  </Blankslate.Description>
                  <Blankslate.PrimaryAction
                    onClick={() => handleGenerateRecap('updated:>@today-1w')}
                    disabled={isGeneratingRecap}
                    className={styles.generateRecapLargeButton}
                  >
                    {isGeneratingRecap ? 'Generating recap...' : 'Generate recap'}
                  </Blankslate.PrimaryAction>
                </Blankslate>
              </div>
            )}
          </div>
        )}

        {selectedTab === 'archive' && (
          <RecapArchive
            recaps={recaps}
            isLoading={isLoading}
            error={error}
            onView={handleViewRecap}
            onDelete={handleDeleteRecap}
          />
        )}
      </div>
    </div>
  )
})
